"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Search, Sparkles, Layers, ArrowRight, ShieldCheck, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
export default function AiVsTraditional() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="aivstraditionalseo" data-nav="light" className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <Badge variant="secondary" className="px-3 py-1 rounded-md text-xs font-semibold tracking-wide uppercase">
            <Sparkles className="size-3.5 text-accent mr-1 inline-block" />
            Strategic scope
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight font-default text-foreground">
            AI Search Optimisation vs Traditional SEO
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            They are not rivals; AI search work extends SEO rather than replacing it. Both disciplines work together to build comprehensive visibility across search engine result pages and conversational answer engines.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Traditional SEO Card */}
          <motion.div variants={itemVariants}>
            <Card className="h-full bg-card text-card-foreground border border-border shadow-md hover:shadow-lg transition-shadow duration-300 rounded-xl p-6 sm:p-8 flex flex-col justify-between">
              <div>
                <CardHeader className="p-0 pb-6 border-b border-border">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 rounded-lg bg-primary/10 border border-primary/20 w-fit">
                      <Search className="size-6 text-primary" />
                    </div>
                    <span className="text-xs font-mono text-muted-foreground font-medium uppercase tracking-wider">
                      Established Foundation
                    </span>
                  </div>
                  <CardTitle className="text-xl sm:text-2xl font-bold font-default text-card-foreground">
                    Traditional SEO
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-2">
                    Rank individual pages to earn direct organic clicks.
                  </p>
                </CardHeader>
                <CardContent className="p-0 pt-6 space-y-5 text-sm sm:text-base leading-relaxed text-muted-foreground">
                  <div>
                    <h3 className="text-base font-semibold text-foreground mb-1">
                      Primary objective
                    </h3>
                    <p>
                      Aims to rank pages in results and earn clicks across commercial and informational queries, from local visibility to comprehensive{" "}
                      <Link to="/NationalSeoPage" className="text-primary font-medium underline underline-offset-2 hover:text-accent transition-colors">
                        National SEO
                      </Link>{" "}
                      campaigns.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-foreground mb-1">
                      Measurement &amp; metrics
                    </h3>
                    <p>
                      Judged by rankings, impressions, clicks, and organic enquiries through established analytics and search console data.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-foreground mb-1">
                      Core pillars
                    </h3>
                    <p>
                      Built on crawlability, relevance, content quality, links, and rigorous{" "}
                      <Link to="/TechnicalSeoPage" className="text-primary font-medium underline underline-offset-2 hover:text-accent transition-colors">
                        technical SEO
                      </Link>{" "}
                      health, using well-understood reporting frameworks.
                    </p>
                  </div>
                </CardContent>
              </div>
            </Card>
          </motion.div>

          {/* AI Search Optimisation Card */}
          <motion.div variants={itemVariants}>
            <Card className="h-full bg-card text-card-foreground border border-border shadow-md hover:shadow-lg transition-shadow duration-300 rounded-xl p-6 sm:p-8 flex flex-col justify-between">
              <div>
                <CardHeader className="p-0 pb-6 border-b border-border">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 rounded-lg bg-primary/10 border border-primary/20 w-fit">
                      <Layers className="size-6 text-primary" />
                    </div>
                    <span className="text-xs font-mono text-muted-foreground font-medium uppercase tracking-wider">
                      Generative Layer
                    </span>
                  </div>
                  <CardTitle className="text-xl sm:text-2xl font-bold font-default text-card-foreground">
                    AI Search Optimisation
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-2">
                    Make your brand clear, retrieved, and cited in generative answers.
                  </p>
                </CardHeader>
                <CardContent className="p-0 pt-6 space-y-5 text-sm sm:text-base leading-relaxed text-muted-foreground">
                  <div>
                    <h3 className="text-base font-semibold text-foreground mb-1">
                      Primary objective
                    </h3>
                    <p>
                      Aims for your brand and pages to be understood, retrieved and, where appropriate, cited inside generated answers across answer engines and assistants.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-foreground mb-1">
                      Measurement &amp; metrics
                    </h3>
                    <p>
                      Judged by sampled mentions, cited sources, referral traffic where available, and enquiries. Reporting is directional because outputs vary between sessions.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-foreground mb-1">
                      Core pillars
                    </h3>
                    <p>
                      Adds focused attention to answer-ready content, clear entity architecture, and external credibility across trusted reference points and knowledge nodes.
                    </p>
                  </div>
                </CardContent>
              </div>
            </Card>
          </motion.div>
        </motion.div>

        {/* What Stays the Same Strip */}
        <div className="mt-12 bg-muted/50 border border-border rounded-xl p-6 sm:p-8">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4 mb-4">
            <span className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
              <CheckCircle2 className="size-5" />
            </span>
            <div>
              <h3 className="text-lg sm:text-xl font-semibold font-default text-foreground">
                What stays the same across both approaches
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground mt-1">
                AI engines still rely heavily on organic web discovery. Foundational SEO best practices remain as critical as ever:
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4 pt-4 border-t border-border/80">
            <div className="p-3 rounded-lg bg-background/80 border border-border/60">
              <span className="text-xs font-semibold uppercase tracking-wider text-accent block mb-1">Crawlability</span>
              <p className="text-xs sm:text-sm text-muted-foreground">Crawlable, indexable pages that search spiders can access without blockers.</p>
            </div>
            <div className="p-3 rounded-lg bg-background/80 border border-border/60">
              <span className="text-xs font-semibold uppercase tracking-wider text-accent block mb-1">Helpful Content</span>
              <p className="text-xs sm:text-sm text-muted-foreground">Original, helpful content that answers real buyer questions thoroughly.</p>
            </div>
            <div className="p-3 rounded-lg bg-background/80 border border-border/60">
              <span className="text-xs font-semibold uppercase tracking-wider text-accent block mb-1">Authority</span>
              <p className="text-xs sm:text-sm text-muted-foreground">Authority, genuine citations, and external trust signals that prove expertise.</p>
            </div>
            <div className="p-3 rounded-lg bg-background/80 border border-border/60">
              <span className="text-xs font-semibold uppercase tracking-wider text-accent block mb-1">Technical Health</span>
              <p className="text-xs sm:text-sm text-muted-foreground">Sound technical architecture, semantic HTML, and structured markup.</p>
            </div>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground mt-4 pt-3 border-t border-border/60">
            Traditional rankings no longer imply AI visibility, but remain an important baseline.
          </p>
        </div>

        {/* Honest Note */}
        <div className="mt-8 p-4 md:p-5 rounded-xl border border-border bg-card/60 max-w-4xl mx-auto flex items-center gap-3">
          <ShieldCheck className="size-5 text-primary shrink-0" />
          <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
            <strong className="text-foreground font-semibold">Honest note:</strong> No work can guarantee a place in AI answers. My focus is on making your content, technical foundations, and brand data as clear and authoritative as possible so models can easily retrieve and reference you.
          </p>
        </div>

        {/* CTA Row */}
        <div className="mt-12 text-center">
          <Button asChild size="lg" className="rounded-md font-medium text-sm">
            <Link to="/ContactPage" className="inline-flex items-center gap-2">
              Book a Free SEO Consultation
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}